/**
 * Shape of values stored in the JSONB columns of `public.brand_profiles`.
 *
 * Postgres CHECK constraints enforce the top-level shape (object / array) but
 * not the contents of array elements, so callers should treat anything beyond
 * the documented fields here as "may exist but is not guaranteed".
 *
 * These types are the canonical narrowed shape used at the read boundary in
 * `context.ts`. New consumers should import from here rather than
 * redefining the same shape.
 */

import type { HostedFontVariant } from "./assets/adobe-fonts";

/** Shape of `brand_profiles.company_information`. */
export type BrandProfileCompanyInformation = {
  name?: string;
  url?: string;
};

/**
 * Shape of `brand_profiles.brand_guidelines_source_file`.
 *
 * Keys mirror the legacy `campaignAgent.brandGuidelinesFile` shape so existing
 * S3 assets remain accessible without rewriting storage keys.
 */
export type BrandGuidelinesSourceFile = {
  filename: string;
  filetype: string;
  filesize?: number;
  s3_key: string;
  description?: string;
};

/**
 * Shape of an entry in `brand_profiles.logos`.
 *
 * Both legacy stores write logos in slightly different shapes:
 *   - Ad Studio's `campaignAgent.logoFiles[]`: `{ fileId, imageUrl?, description? }`
 *   - Lifecycle's `content_settings.brand_guidelines.logo`: `{ id, name, s3_key?, alt_text?, url? }`
 *
 * The backfill helper normalizes both into this shape; in particular, the
 * storage prefix lives in `fileId` (Ad Studio convention) or `s3Key` (lifecycle
 * convention), preserved as-is so existing assets resolve.
 */
export type BrandLogo = {
  /** Stable per-entry uuid that CEP rows reference; storage keys change on re-upload. */
  id?: string;
  fileId?: string;
  s3Key?: string;
  imageUrl?: string;
  url?: string;
  /** Unauthenticated copy for email recipients, since `imageUrl` needs a session. SVGs are served as PNG. */
  publicUrl?: string;
  name?: string;
  description?: string;
  altText?: string;
  /** Media type of the file served at `publicUrl`. */
  mimeType?: string;
  /** Size in bytes of the file served at `publicUrl`. */
  sizeBytes?: number;
  /** Set on at most one logo per profile; absent, never false, on the others. */
  isProfileLogo?: true;
};

/** The logo fields that can only be learned by reading the stored bytes. */
export type StoredLogoFields = Pick<
  BrandLogo,
  "publicUrl" | "mimeType" | "sizeBytes"
>;

/**
 * Google Fonts variant strings — e.g. "regular", "700", "700italic".
 */
export type BrandFontVariant = string;

/** A variable font's design axes, keyed by tag ("wght", "opsz", …). */
export type VariationAxes = Record<
  string,
  { name: string; min: number; default: number; max: number }
>;

/** A variable font's named instances: instance name → axis coordinates. */
export type NamedVariations = Record<string, Record<string, number>>;

/**
 * Shape of an entry in `brand_profiles.fonts.files` — an uploaded font asset.
 */
export type BrandFontFile = {
  fileId: string;
  fontUrl?: string;
  fileName?: string;
  fallbackFont?: string;
  /**
   * Public, recipient-safe CloudFront URL for the font bytes. Distinct from the private
   * `fontUrl`, which points at `/api/agents/files` and 401s for email recipients.
   * Note that we will make `publicFontUrl` a required field after the backfill is complete.
   */
  publicFontUrl?: string;
  /**
   * Typographic identity parsed from the font binary at upload (see
   * `parseFontMetadata`). When present, renderers register the face under
   * `familyName` + `weight`/`style` descriptors so files like
   * `Geist-Black.woff2` resolve `font-family: "Geist"; font-weight: 900` —
   * in addition to the legacy filename-derived family, which existing
   * content still references. Absent on entries uploaded before parsing
   * existed or whose binary was unparseable; those fall back to
   * filename-derived registration only.
   */
  familyName?: string;
  /** CSS `font-weight` value: "700", or "100 900" for a variable font. */
  weight?: string;
  /** CSS `font-style` value: "normal" | "italic". */
  style?: string;
  /**
   * ISO timestamp set when the font is retired (soft-deleted). The entry stays
   * in the array at its original index and keeps resolving — ads reference
   * fonts by bare family-name string, so removing the entry would silently
   * fall those ads back to a default face. Retired entries are hidden only
   * where a human *chooses* a font. See `assets/font-retirement.ts`.
   */
  deletedAt?: string | null;
  /**
   * The face's own PostScript name (nameID 6), e.g. `Avenir-Heavy`. The name
   * Photoshop matches a PSD text layer against, so the Ad Studio PSD export
   * writes it verbatim rather than reconstructing a spelling. Absent on
   * entries parsed before this field existed (`needsFontReparse` selects
   * them) or whose binary declares no legal name.
   */
  postscriptName?: string;
  /** Raw typographic family (nameID 16), when the binary declares one. */
  preferredFamily?: string;
  /** Raw typographic subfamily (nameID 17), when the binary declares one. */
  preferredSubfamily?: string;
  variationAxes?: VariationAxes;
  namedVariations?: NamedVariations;
  scopes?: BrandFontScope[];
};

export type BrandFontFamilyType = "google" | "web-safe" | "custom";

export const BRAND_FONT_SCOPES = ["native-delivery", "agents"] as const;

/** A surface a font family is limited to. An unscoped family applies everywhere. */
export type BrandFontScope = (typeof BRAND_FONT_SCOPES)[number];

/**
 * Custom-font URL binding (one entry per weight/style).
 */
export type BrandFontCustomVariant = {
  id?: string;
  url: string;
  weight?: string;
  style?: string;
};

/**
 * Shape of an entry in `brand_profiles.fonts.families` — a logical font
 * declaration. Migrated from lifecycle's
 * `content_settings.brand_guidelines.font_groups`.
 */
export type BrandFontFamily = {
  id: string;
  name: string;
  type: BrandFontFamilyType;
  variants?: BrandFontVariant[];
  customVariants?: BrandFontCustomVariant[];
  stylesheetUrl?: string;
  stylesheetVariants?: HostedFontVariant[];
  scopes?: BrandFontScope[];
  /** Marks the font native delivery emails default to. At most one family carries it. */
  isEmailDefault?: boolean;
  fallbackFont?: string;
  description?: string;
  /** Retirement tag — see `deletedAt` on `BrandFontFile`. */
  deletedAt?: string | null;
};

/** Shape of `brand_profiles.fonts`. */
export type BrandFonts = {
  files: BrandFontFile[];
  families: BrandFontFamily[];
};

/**
 * Shape of `brand_profiles.email_style_configuration` (nullable).
 *
 * Feeds the Hightouch canonical responsive style block that wraps email
 * components at render time: `mobileBreakpointWidth` sets the mobile media-query
 * breakpoint, and `contentWidthPx` sets the centered desktop content column
 * width the standalone component wrapper caps at.
 *
 * `mobileBreakpointWidth` (when the mobile layout kicks in) and `contentWidthPx`
 * (how wide the desktop content column is) are independent knobs — a 600px
 * breakpoint does not imply a 600px content width.
 *
 * Written via the brand-profile settings UI through Hasura (no backend write
 * path), so it is not guaranteed well-formed — read it through
 * `resolveEmailStyleConfiguration` (see `email-style-config.ts`).
 */
export type EmailStyleConfiguration = {
  mobileBreakpointWidth: number;
  contentWidthPx?: number;
  /** Whether email links default to underlined. Missing means false. */
  underlineLinks?: boolean;
};

/**
 * Shape of an entry in `brand_profiles.colors` — a named brand color the user
 * sets in the brand color picker. `hex` is a `#RRGGBB` string. These feed the
 * deterministic brand-color correction applied to generated ad mockups.
 */
export type BrandColor = {
  hex: string;
  name?: string;
  /** Email elements that default to this color. Each role sits on at most one color in the palette. */
  roles?: BrandColorRole[];
};

/** An email element that takes a default brand color. */
export type BrandColorRole =
  | "text"
  | "link"
  | "button.label"
  | "button.background"
  | "content.background"
  | "page.background";

/**
 * Resolved brand-profile context returned by `getBrandProfileContext`.
 *
 * Field names use camelCase (DB columns are snake_case). Nullable text columns
 * are surfaced as `string | null`; missing / empty JSONB collections are
 * surfaced as empty arrays / objects.
 */
export type BrandProfileContext = {
  id: string;
  workspaceId: number;
  name: string;
  companyInformation: BrandProfileCompanyInformation;
  businessContext: string | null;
  brandGuidelines: string | null;
  brandGuidelinesSourceFile: BrandGuidelinesSourceFile | null;
  logos: BrandLogo[];
  fonts: BrandFonts;
  colors: BrandColor[];
  allowImageGeneration: boolean;
  isDefault: boolean;
  // Staleness token for review-gated brand profile writes. ISO via Date#toISOString.
  updatedAt: Date;
};

/** Input to `createBrandProfile`. */
export type CreateBrandProfileInput = {
  workspaceId: number;
  name: string;
  /** Omit to become the default only when the workspace has none yet. */
  isDefault?: boolean;
  companyInformation?: BrandProfileCompanyInformation;
  businessContext?: string | null;
  brandGuidelines?: string | null;
  colors?: BrandColor[];
  logos?: BrandLogo[];
  fonts?: BrandFonts;
  emailStyleConfiguration?: EmailStyleConfiguration;
  createdBy?: number | null;
};

/**
 * The columns a review-gated update may replace. An absent field leaves its
 * column untouched; a present one replaces it wholesale, except fonts, which
 * merge (see `assets/font-merge.ts` — fonts are retired, never removed).
 */
export type BrandProfileUpdate = {
  colors?: BrandColor[];
  fonts?: {
    families?: BrandFontFamily[];
    files?: BrandFontFile[];
  };
  logos?: BrandLogo[];
};

export type ApplyBrandProfileUpdateResult =
  | { status: "applied"; id: string }
  | { status: "stale"; id: string | null };

/** An applied write reports the `updated_at` it moved the profile from and to. */
export type ApplyBrandImageGuardrailsUpdateResult =
  | { status: "applied"; previousUpdatedAt: Date; updatedAt: Date }
  | { status: "unchanged" }
  | { status: "stale" };

export interface AvailableBrandProfile {
  /** The id `generate_ads` accepts as `brandProfileId`. */
  brandProfileId: string;
  name: string;
  /** The one used when `generate_ads` is called without a `brandProfileId`. */
  isDefault: boolean;
}

export interface BrandProfileAssignmentDefaults {
  default_assignment_source: string | null;
  esp_destination_id: string | null;
  esp_target_kind: string | null;
}
